import React, { useEffect, useRef } from 'react';
import noise from '../assets/heroshader/noise.png';
import logo from '../assets/heroshader/logo.png';

const HeroShader = () => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const glRef = useRef(null);
  const mouseRef = useRef({ x: 0.5, y: 0.5, vx: 0, vy: 0, prevX: 0.5, prevY: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Handle context loss/restore
    const handleContextLost = (e) => {
      e.preventDefault();
      console.log('WebGL context lost');
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };

    const handleContextRestored = () => {
      console.log('WebGL context restored');
      // Context will be recreated on next render
    };

    canvas.addEventListener('webglcontextlost', handleContextLost);
    canvas.addEventListener('webglcontextrestored', handleContextRestored);

    const gl = canvas.getContext('webgl', { 
      alpha: false, 
      antialias: false,
      preserveDrawingBuffer: false 
    });
    
    if (!gl) {
      console.error('WebGL not supported');
      return;
    }

    glRef.current = gl;

    // Vertex Shader
    const vertexShaderSource = `
      #ifdef GL_FRAGMENT_PRECISION_HIGH
      precision highp float;
      #else
      precision mediump float;
      #endif
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
          vUv = 0.5 * aPosition + 0.5;
          gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    // Trail Fragment Shader (the actual one from your document)
    const trailFragmentShaderSource = `
      #ifdef GL_FRAGMENT_PRECISION_HIGH
      precision highp float;
      #else
      precision mediump float;
      #endif

      varying vec2 vUv;
      uniform float uTime;
      uniform float uDeltaTime;
      uniform float uLogoScale;
      uniform vec2 uMouse;
      uniform vec2 uMouseVelocity;
      uniform vec2 uResolution;
      uniform sampler2D uNoiseTexture;
      uniform sampler2D uPreviousFrame;
      uniform sampler2D uLogoTexture;

      #define TRAIL_FALLOFF 9000.0
      #define FADE_EXP vec4(0.02, 0.02, 0.1, 0.1)
      #define SCROLL_SPEED 0.0005
      #define DISTORT_SPEED 0.02
      #define LOGO_TWIRL 0.4
      #define LOGO_PULL 0.1
      #define LOGO_SCALE 0.5
      #define LOGO_RATIO 2.08
      #define TURB_NUM 8.0
      #define TURB_AMP 0.6
      #define TURB_SPEED 0.5
      #define TURB_VEL vec2(0.1, 0.0)
      #define TURB_FREQ 50.0
      #define TURB_EXP 1.3

      vec2 turbulence(vec2 p) {
          mat2 rot = mat2(0.6, -0.8, 0.8, 0.6);
          vec2 turb = vec2(0.0);
          float freq = TURB_FREQ;
          for (float i = 0.0; i < 8.0; i++) {
              vec2 pos = p + TURB_SPEED * i * uTime * TURB_VEL;
              float phase = freq * (pos * rot).y + TURB_SPEED * uTime * freq * 0.1;
              turb += rot[0] * sin(phase) / freq;
              rot *= mat2(0.6, -0.8, 0.8, 0.6);
              freq *= TURB_EXP;
          }
          return turb;
      }

      void main() {
          vec2 ratio = min(uResolution.yx / uResolution.xy, 1.0);
          vec2 scale = max(uLogoScale, 1.0 - (LOGO_RATIO / 4.0)) * ratio * vec2(LOGO_RATIO, -1.0);
          vec2 logoUV = 0.5 + (vUv - 0.5) / scale;
          
          vec4 logo = vec4(0);
          if (logoUV.x >= 0.0 && logoUV.x <= 1.0 && logoUV.y >= 0.0 && logoUV.y <= 1.0) {
              logo = texture2D(uLogoTexture, logoUV);
          }

          float delta = 144.0 * uDeltaTime;
          vec2 scroll = SCROLL_SPEED * vec2(1.0, vUv.y - 0.5) * ratio;
          vec2 turb = turbulence((vUv + scroll) / ratio);
          vec2 distort = DISTORT_SPEED * turb;
          distort -= LOGO_TWIRL * (logo.rg - 0.6) * mat2(0, -1, 1, 0) * (logo.g - 0.5) * logo.b;
          distort -= LOGO_PULL * (logo.rg - 0.6) * logo.b * logo.b;
          vec2 distortedUv = vUv + delta * scroll + delta * distort * ratio;

          vec4 prev = texture2D(uPreviousFrame, distortedUv);

          vec2 trailA = vUv + 0.01 * delta * turb * ratio - uMouse;
          vec2 trailB = -uMouseVelocity;
          float trailD = dot(trailB, trailB);
          vec2 trailDif = trailA / ratio;
          float falloff = 0.0;
          
          if (trailD > 0.0) {
              float f = clamp(dot(trailA, trailB) / trailD, 0.0, 1.0);
              trailDif -= f * trailB / ratio;
              falloff = (1.0 - logo.b) / (1.0 + TRAIL_FALLOFF * dot(trailDif, trailDif));
              falloff *= min(trailD / (0.001 + trailD), 1.0);
          }

          vec2 suv = (uMouse - uMouseVelocity) * 2.0 - 1.0;
          float vig = 1.0 - abs(suv.y);
          vig *= 0.5 + 0.5 * suv.x;

          vec2 nuv = gl_FragCoord.xy / 64.0 + uTime * vec2(7.1, 9.1);
          float noise = texture2D(uNoiseTexture, nuv).r;

          vec4 fade = pow(vec4(noise), FADE_EXP);
          fade = exp(-2.0 * fade * uDeltaTime);
          vec4 decay = mix(vec4(0.5, 0.5, 0.0, 0.0), prev, fade);

          vec4 col = decay;
          vec2 vel = (-trailB) / (0.01 + length(trailB));
          col.rg -= (0.5 - abs(decay.rg - 0.5)) * (falloff * vel);
          col.ba += falloff * (1.0 - decay.ba) * vec2(1.0, vig * vig);
          col += (noise - 0.5) / 255.0;
          
          gl_FragColor = col;
      }
    `;

    // Main Fragment Shader (from your first document)
    const mainFragmentShaderSource = `
      #ifdef GL_FRAGMENT_PRECISION_HIGH
      precision highp float;
      #else
      precision mediump float;
      #endif

      varying vec2 vUv;
      uniform float uTime;
      uniform vec2 uResolution;
      uniform float uLogoScale;
      uniform vec2 uOffset;
      uniform sampler2D uNoiseTexture;
      uniform sampler2D uLogoTexture;
      uniform sampler2D uTrailTexture;

      #define STAR 5.0
      #define FLARE 4.0
      #define COLOR vec3(0.2, 0.3, 0.8)
      #define STAR_NUM 12.0
      #define STAR_AMP 0.5
      #define STAR_SPEED 0.01
      #define STAR_VEL vec2(1.0, 0.0)
      #define STAR_FREQ 8.0
      #define STAR_EXP 1.5
      #define LOGO_SCALE 0.5
      #define LOGO_RATIO 2.08
      #define GLOW_STRENGTH 12.0
      #define GLOW_RED vec3(0.5, 0.2, 0.2)
      #define GLOW_BLUE vec3(0.3, 0.3, 0.6)
      #define GLOW_TURBULENCE 0.4
      #define GLOW_TINT 3.0
      #define LIGHT_EXP 30.0
      #define TRAIL_EXP vec3(1.4, 1.2, 1.0)
      #define TRAIL_STRENGTH 0.4
      #define DITHER 0.01
      #define DITHER_RES 64.0

      vec3 gamma_encode(vec3 lrgb) {
          return sqrt(lrgb);
      }

      vec2 turbulence(vec2 p, float freq, float num) {
          mat2 rot = mat2(0.6, -0.8, 0.8, 0.6);
          vec2 turb = vec2(0.0);
          for (float i = 0.0; i < 12.0; i++) {
              if (i >= num) break;
              vec2 pos = p + turb + STAR_SPEED * i * uTime * STAR_VEL;
              float phase = freq * (pos * rot).y + STAR_SPEED * uTime * freq;
              turb += rot[0] * sin(phase) / freq;
              rot *= mat2(0.6, -0.8, 0.8, 0.6);
              freq *= STAR_EXP;
          }
          return turb;
      }

      vec3 star(inout vec2 p) {
          #define STAR_STRETCH 0.7
          #define STAR_CURVE 0.5
          
          vec2 suv = p * 2.0 - 1.0;
          vec2 right = suv - vec2(1.0, 0.0);
          right.x *= STAR_STRETCH * uResolution.x / uResolution.y;
          
          float factor = 1.0 + 0.4 * sin(9.0 * suv.y) * sin(5.0 * (suv.x + 5.0 * uTime * STAR_SPEED));
          vec2 turb = right + factor * STAR_AMP * turbulence(right, STAR_FREQ, STAR_NUM);
          turb.x -= STAR_CURVE * suv.y * suv.y;
          
          float fade = max(4.0 * suv.y * suv.y - suv.x + 1.2, 0.001);
          float atten = fade * max(0.5 * turb.x, -turb.x);
          
          float ft = 0.4 * uTime;
          vec2 fp = 8.0 * (turb + 0.5 * STAR_VEL * ft);
          fp *= mat2(0.4, -0.3, 0.3, 0.4);
          float f = cos(fp.x) * sin(fp.y) - 0.5;
          float flare = f * f + 0.5 * suv.y * suv.y - 1.5 * turb.x + 0.6 * cos(0.42 * ft + 1.6 * turb.y) * cos(0.31 * ft - turb.y);
          
          vec3 col = 0.1 * COLOR * (STAR / (atten * atten) + FLARE / (flare * flare));
          
          const vec3 chrom = vec3(0.0, 0.1, 0.2);
          col *= exp(p.x * cos(turb.y * 5.0 + 0.4 * (uTime + turb.x * 1.0) + chrom) * cos(turb.y * 7.0 - 0.5 * (uTime - turb.x * 1.5) + chrom) * cos(turb.y * 9.0 + 0.6 * (uTime + turb.x * 2.0) + chrom));
          
          return col;
      }

      void main() {
          vec2 duv = 0.9 * gl_FragCoord.xy / DITHER_RES * mat2(0.8, -0.6, 0.6, 0.8);
          float dither = texture2D(uNoiseTexture, duv).r - 0.5;
          
          vec2 ratio = min(uResolution.yx / uResolution.xy, 1.0);
          vec4 trailTex = texture2D(uTrailTexture, vUv);
          vec2 suv = vUv * 2.0 - 1.0;
          
          vec2 scale = max(uLogoScale, 1.0 - (LOGO_RATIO / 4.0)) * ratio * vec2(LOGO_RATIO, -1.0);
          vec2 logoUv = 0.5 + (vUv - 0.5) / scale;
          
          vec4 logo = vec4(0);
          vec4 logoTurb = vec4(0);
          vec2 dir = vec2(0);
          float glow = 0.0;
          vec2 distort = uOffset;
          
          if (logoUv.x >= 0.0 && logoUv.x <= 1.0 && logoUv.y >= 0.0 && logoUv.y <= 1.0) {
              logo = texture2D(uLogoTexture, logoUv);
              dir = (logo.rg - 0.6);
              dir.x = -dir.x;
              
              vec2 shift = -2.0 * vec2(dir.y, -dir.x) * dir.y * logo.b;
              shift += 0.1 * (1.0 - logo.b) * (trailTex.rg - 0.5) * trailTex.b * ratio;
              vec2 logoT = (logoUv) * vec2(LOGO_RATIO, 1.0) + shift;
              logoT += (1.0 - logo.b) * turbulence(logoT, 40.0, 6.0);
              logoUv = (logoT - shift) / vec2(LOGO_RATIO, 1.0);
              
              logoTurb = texture2D(uLogoTexture, logoUv);
              logoTurb.b = mix(logo.b, logoTurb.b, GLOW_TURBULENCE);
              
              float xx = logoUv.x;
              float yy = (logoUv.y - 0.5);
              glow = max(logoTurb.b - (xx * xx + 8.0 * yy * yy) * logoTurb.b, 0.0);
              
              distort += dir * logo.b * (1.0 - logo.b);
          }
          
          vec2 starUv = vUv + distort;
          starUv += 0.3 * (trailTex.rg - 0.5) * trailTex.b * ratio;
          vec3 col = star(starUv);
          
          float vig = 1.0 - abs(suv.y);
          vig *= 0.5 + 0.5 * suv.x;
          col *= vig * vig;
          
          col /= 1.0 + col;
          col = clamp(col, 0.0, 1.0);
          col = gamma_encode(col);
          
          float yy = suv.y + 0.03;
          yy = max(1.0 - 1e1 * yy * yy / max(0.5 + 1.5 * starUv.x, 0.1), 0.0);
          float light = max(0.5 + 0.5 * starUv.x, 0.0) * yy;
          light += 2.0 * (1.0 - light) * glow;
          
          float tint = GLOW_TINT * dir.x * glow;
          vec3 hue = mix(GLOW_RED, GLOW_BLUE, 1.0 + suv.x + tint);
          float alpha = 1.0 - (1.0 - pow(yy, LIGHT_EXP)) * glow;
          vec3 rim = GLOW_STRENGTH * light * light * light * light * alpha * (0.5 + 0.5 * suv.x) * hue;
          
          rim /= (1.0 + rim);
          col += (1.0 - col) * rim * rim;
          col += TRAIL_STRENGTH * hue * pow(trailTex.aaa, TRAIL_EXP);
          
          // Logo mask - COMMENTED OUT FOR TESTING
          // float a = smoothstep(0.2, 1.0, logo.a);
          // col.rgb = a * col.rgb + (1.0 - a);
          
          col += DITHER * dither;
          
          gl_FragColor = vec4(col, 1.0);
      }
    `;

    // Compile shader
    function compileShader(source, type) {
      const shader = gl.createShader(type);
      if (!shader) {
        console.error('Failed to create shader');
        return null;
      }
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      
      const success = gl.getShaderParameter(shader, gl.COMPILE_STATUS);
      if (!success) {
        const info = gl.getShaderInfoLog(shader);
        console.error('Shader compilation error:', info);
        console.error('Shader type:', type === gl.VERTEX_SHADER ? 'VERTEX' : 'FRAGMENT');
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    }

    // Create program
    function createProgram(vertSource, fragSource) {
      const vertShader = compileShader(vertSource, gl.VERTEX_SHADER);
      const fragShader = compileShader(fragSource, gl.FRAGMENT_SHADER);
      
      if (!vertShader || !fragShader) return null;
      
      const program = gl.createProgram();
      gl.attachShader(program, vertShader);
      gl.attachShader(program, fragShader);
      gl.linkProgram(program);
      
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        console.error('Program linking error:', gl.getProgramInfoLog(program));
        return null;
      }
      
      return program;
    }

    const trailProgram = createProgram(vertexShaderSource, trailFragmentShaderSource);
    const mainProgram = createProgram(vertexShaderSource, mainFragmentShaderSource);
    
    if (!trailProgram || !mainProgram) {
      console.error('Failed to create shader programs');
      return;
    }

    // Setup geometry
    const positions = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, positions, gl.STATIC_DRAW);

    // Get uniform locations for trail program
    const trailUniforms = {
      uTime: gl.getUniformLocation(trailProgram, 'uTime'),
      uDeltaTime: gl.getUniformLocation(trailProgram, 'uDeltaTime'),
      uLogoScale: gl.getUniformLocation(trailProgram, 'uLogoScale'),
      uMouse: gl.getUniformLocation(trailProgram, 'uMouse'),
      uMouseVelocity: gl.getUniformLocation(trailProgram, 'uMouseVelocity'),
      uResolution: gl.getUniformLocation(trailProgram, 'uResolution'),
      uNoiseTexture: gl.getUniformLocation(trailProgram, 'uNoiseTexture'),
      uPreviousFrame: gl.getUniformLocation(trailProgram, 'uPreviousFrame'),
      uLogoTexture: gl.getUniformLocation(trailProgram, 'uLogoTexture'),
    };

    // Get uniform locations for main program
    const mainUniforms = {
      uTime: gl.getUniformLocation(mainProgram, 'uTime'),
      uResolution: gl.getUniformLocation(mainProgram, 'uResolution'),
      uLogoScale: gl.getUniformLocation(mainProgram, 'uLogoScale'),
      uOffset: gl.getUniformLocation(mainProgram, 'uOffset'),
      uNoiseTexture: gl.getUniformLocation(mainProgram, 'uNoiseTexture'),
      uLogoTexture: gl.getUniformLocation(mainProgram, 'uLogoTexture'),
      uTrailTexture: gl.getUniformLocation(mainProgram, 'uTrailTexture'),
    };

    // Create framebuffer for trail texture ping-pong
    const trailFBO1 = gl.createFramebuffer();
    const trailFBO2 = gl.createFramebuffer();
    const trailTex1 = gl.createTexture();
    const trailTex2 = gl.createTexture();

    function setupTrailTexture(texture) {
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, canvas.width, canvas.height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    }

    setupTrailTexture(trailTex1);
    setupTrailTexture(trailTex2);

    gl.bindFramebuffer(gl.FRAMEBUFFER, trailFBO1);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, trailTex1, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, trailFBO2);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, trailTex2, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Load textures
    const textures = {
      noise: null,
      logo: null,
    };

    let texturesLoaded = 0;
    const totalTextures = 2;

    function loadTexture(url, name) {
      const texture = gl.createTexture();
      const image = new Image();
      
      image.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.REPEAT);
        
        textures[name] = texture;
        texturesLoaded++;
        
        if (texturesLoaded === totalTextures) {
          startAnimation();
        }
      };
      
      image.onerror = () => {
        console.error(`Failed to load texture: ${url}`);
      };
      
      image.src = url;
    }

    loadTexture(noise, 'noise');
    loadTexture(logo, 'logo');

    // Mouse tracking
    function handleMouseMove(e) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX / rect.width;
      const y = 1.0 - (e.clientY / rect.height);
      
      const mouse = mouseRef.current;
      mouse.vx = (x - mouse.x) * 0.5;
      mouse.vy = (y - mouse.y) * 0.5;
      mouse.x = x;
      mouse.y = y;
    }

    window.addEventListener('mousemove', handleMouseMove);

    // Animation
    let currentTrailSource = 0;
    let startTime = Date.now();
    let lastTime = startTime;

    function resize() {
      const dpr = Math.min(window.devicePixelRatio, 2);
      const w = window.innerWidth * dpr;
      const h = window.innerHeight * dpr;
      
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        canvas.style.width = window.innerWidth + 'px';
        canvas.style.height = window.innerHeight + 'px';
        
        setupTrailTexture(trailTex1);
        setupTrailTexture(trailTex2);
      }
    }

    function render() {
      if (!textures.noise || !textures.logo) return;
      
      const currentTime = Date.now();
      const time = (currentTime - startTime) * 0.001;
      const deltaTime = Math.min((currentTime - lastTime) * 0.001, 0.1);
      lastTime = currentTime;
      
      resize();
      
      const mouse = mouseRef.current;
      mouse.vx *= 0.95;
      mouse.vy *= 0.95;
      
      gl.viewport(0, 0, canvas.width, canvas.height);
      
      // Render trail pass
      const trailSource = currentTrailSource === 0 ? trailTex1 : trailTex2;
      const trailTarget = currentTrailSource === 0 ? trailFBO2 : trailFBO1;
      
      gl.bindFramebuffer(gl.FRAMEBUFFER, trailTarget);
      gl.useProgram(trailProgram);
      
      const aPos = gl.getAttribLocation(trailProgram, 'aPosition');
      gl.enableVertexAttribArray(aPos);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
      
      gl.uniform1f(trailUniforms.uTime, time);
      gl.uniform1f(trailUniforms.uDeltaTime, deltaTime);
      gl.uniform1f(trailUniforms.uLogoScale, 0.5);
      gl.uniform2f(trailUniforms.uMouse, mouse.x, mouse.y);
      gl.uniform2f(trailUniforms.uMouseVelocity, mouse.vx, mouse.vy);
      gl.uniform2f(trailUniforms.uResolution, canvas.width, canvas.height);
      
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textures.noise);
      gl.uniform1i(trailUniforms.uNoiseTexture, 0);
      
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, trailSource);
      gl.uniform1i(trailUniforms.uPreviousFrame, 1);
      
      gl.activeTexture(gl.TEXTURE2);
      gl.bindTexture(gl.TEXTURE_2D, textures.logo);
      gl.uniform1i(trailUniforms.uLogoTexture, 2);
      
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      // Render main pass to screen
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.useProgram(mainProgram);
      
      const aPos2 = gl.getAttribLocation(mainProgram, 'aPosition');
      gl.enableVertexAttribArray(aPos2);
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
      gl.vertexAttribPointer(aPos2, 2, gl.FLOAT, false, 0, 0);
      
      gl.uniform1f(mainUniforms.uTime, time);
      gl.uniform2f(mainUniforms.uResolution, canvas.width, canvas.height);
      gl.uniform1f(mainUniforms.uLogoScale, 0.5);
      gl.uniform2f(mainUniforms.uOffset, 0.0, 0.0);
      
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, textures.noise);
      gl.uniform1i(mainUniforms.uNoiseTexture, 0);
      
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, textures.logo);
      gl.uniform1i(mainUniforms.uLogoTexture, 1);
      
      gl.activeTexture(gl.TEXTURE2);
      const currentTrailTexture = currentTrailSource === 0 ? trailTex2 : trailTex1;
      gl.bindTexture(gl.TEXTURE_2D, currentTrailTexture);
      gl.uniform1i(mainUniforms.uTrailTexture, 2);
      
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      
      currentTrailSource = 1 - currentTrailSource;
      
      animationRef.current = requestAnimationFrame(render);
    }

    function startAnimation() {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      render();
    }

    resize();
    window.addEventListener('resize', resize);

    return () => {
      canvas.removeEventListener('webglcontextlost', handleContextLost);
      canvas.removeEventListener('webglcontextrestored', handleContextRestored);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      // Don't force context loss, let it clean up naturally
    };
  }, []);

  return (
    <div className="absolute inset-0 w-full h-full">
      <canvas
        ref={canvasRef}
        className="absolute top-0 left-0 w-full h-full"
        style={{ zIndex: 0 }}
      />
    </div>
  );
};

export default HeroShader;